/**
 * Section 5 of the Check-In tab — only rendered when something is off:
 * roles missing from the check-in tab, incomplete rows, disagreeing
 * prioritisation, orphaned rows, and degraded capacity / pipeline feeds.
 * Worth fixing in the sheet before the next check-in.
 */
import type { CheckInData } from '../../../types/taCheckin';
import { recruiterBase, sheetInternFte, warehouseAgeHours } from '../../../utils/taCheckinIndex';
import { round1 } from './checkinUi';

interface HygieneItem {
  sev: 'crit' | 'warn' | 'info';
  t: string;
  s: string;
}

const DOT: Record<HygieneItem['sev'], string> = { crit: '#dc2626', warn: '#e88f3d', info: '#979797' };

export function collectHygiene(data: CheckInData): HygieneItem[] {
  const items: HygieneItem[] = [];
  const roles = data.roles;
  const checkinRows = data.checkinRows;

  const missing = roles.filter((r) => !r.inSheet);
  if (missing.length) {
    items.push({
      sev: 'crit',
      t: `${missing.length} open role${missing.length === 1 ? '' : 's'} in the hiring plan but missing from the check-in tab`,
      s:
        missing
          .slice(0, 4)
          .map((r) => `${r.title}${r.positionId ? ` (${r.positionId})` : ' — no Position ID'}`)
          .join(' · ') + (missing.length > 4 ? ' …' : ''),
    });
  }

  const incomplete = checkinRows.filter((r) => !r.urgency || !r.prioritisation || !r.startDate);
  if (incomplete.length) {
    items.push({
      sev: 'warn',
      t: `${incomplete.length} check-in row${incomplete.length === 1 ? '' : 's'} with incomplete data`,
      s:
        incomplete
          .slice(0, 4)
          .map((r) => {
            const gaps = [
              !r.urgency && 'urgency',
              !r.prioritisation && 'prioritisation',
              !r.startDate && 'start date',
            ].filter(Boolean);
            return `${r.title || '(untitled)'} (${r.roleId || '—'}): no ${gaps.join(', ')}`;
          })
          .join(' · ') + (incomplete.length > 4 ? ' …' : ''),
    });
  }

  const mismatch = roles.filter(
    (r) => r.inSheet && r.sheetPriority && r.planPriority && r.sheetPriority !== r.planPriority
  );
  if (mismatch.length) {
    items.push({
      sev: 'warn',
      t: `${mismatch.length} role${mismatch.length === 1 ? '' : 's'} where check-in prioritisation ≠ hiring plan rating`,
      s:
        mismatch
          .slice(0, 4)
          .map((r) => `${r.title}: check-in ${r.sheetPriority} vs plan ${r.planPriority}`)
          .join(' · ') + (mismatch.length > 4 ? ' …' : ''),
    });
  }

  const orphan = checkinRows.filter(
    (r) => r.roleId && !roles.some((a) => String(a.positionId) === String(r.roleId))
  );
  if (orphan.length) {
    items.push({
      sev: 'info',
      t: `${orphan.length} check-in row${orphan.length === 1 ? '' : 's'} not open in the hiring plan`,
      s:
        orphan
          .slice(0, 4)
          .map((r) => `${r.title || '(untitled)'} (${r.roleId})`)
          .join(' · ') + (orphan.length > 4 ? ' …' : ''),
    });
  }

  const noCap = data.clusters.filter((c) => recruiterBase(data, c) == null);
  if (noCap.length) {
    items.push({
      sev: 'warn',
      t: `No capacity figure available for ${noCap.join(', ')}`,
      s: 'Needs either a Cluster FTE row on the TA Capacity Setup tab, or both Open Roles and Cluster Role Load on the check-in tab. Without it role load and interviews per FTE cannot be scored.',
    });
  }

  const cap = data.capacity;
  if (!cap.ok) {
    items.push({
      sev: 'warn',
      t: 'Recruiter/intern capacity split unavailable — intern shifting is approximate',
      s: `${cap.error || 'No capacity data.'} Capacity is being derived from the check-in tab instead, so the recruiter baseline may be slightly off.`,
    });
  } else {
    if (!cap.isCurrentMonth) {
      items.push({
        sev: 'warn',
        t: `Capacity figures are from ${cap.month}, not the current month`,
        s: 'The TA Capacity Setup tab has no filled column for this month, so the most recent one is used.',
      });
    }
    const sumI = data.clusters.reduce((s, c) => s + (sheetInternFte(data, c) || 0), 0);
    if (cap.internsTotal != null && Math.abs(sumI - cap.internsTotal) > 0.05) {
      items.push({
        sev: 'info',
        t: 'The sheet’s per-cluster intern split does not add up to its intern total',
        s: `Clusters sum to ${round1(sumI)} against a TA Interns figure of ${cap.internsTotal} for ${cap.month}.`,
      });
    }
  }

  const pl = data.pipeline;
  if (!pl.ok) {
    items.push({
      sev: 'crit',
      t: 'Pipeline risk is not in the index — the warehouse could not be queried',
      s: `${pl.error || 'No pipeline data.'} Its ${data.config.index_weights?.pipeline_health ?? 20}% weight is redistributed across the other criteria.`,
    });
  } else {
    if (pl.unresolved?.length) {
      items.push({
        sev: 'warn',
        t: `${pl.unresolved.length} Lever posting${pl.unresolved.length === 1 ? '' : 's'} could not be attributed to a cluster`,
        s:
          pl.unresolved
            .slice(0, 4)
            .map((u) => `"${u.title}" (${u.recruiter || 'no recruiter'}) — ${u.reason}`)
            .join(' · ') +
          (pl.unresolved.length > 4 ? ' …' : '') +
          ' · add a rule to the posting-cluster map or map the recruiter',
      });
    }
    const emptyClusters = data.clusters.filter((c) => !pl.byCluster?.[c]?.postings);
    if (emptyClusters.length) {
      items.push({
        sev: 'warn',
        t: `No Lever postings attributed to ${emptyClusters.join(', ')}`,
        s: 'Pipeline risk is excluded for that cluster, so its index is built from the remaining criteria.',
      });
    }
    const ageHours = warehouseAgeHours(data);
    if (ageHours != null && ageHours > 36) {
      items.push({
        sev: 'info',
        t: `Warehouse Lever data is ${Math.round(ageHours / 24)} days old`,
        s: `The newest interview row synced ${ageHours}h ago. The nightly DWH job may not have run — Refresh re-queries the warehouse, but cannot make it ingest from Lever.`,
      });
    }
  }

  // Interview load can be unavailable while the sheet still carries a stale
  // weekly number — the index then silently uses that older figure, so say so.
  if (data.interviewLoad && !data.interviewLoad.ok) {
    items.push({
      sev: 'warn',
      t: 'Interview load is the weekly sheet figure, not a live count',
      s: `${data.interviewLoad.error || 'The warehouse count is unavailable.'} Refresh re-reads the sheet, so the number only changes when the weekly interview-load agent writes a new one.`,
    });
  }

  return items;
}

export function DataHygiene({ data }: { data: CheckInData }) {
  const items = collectHygiene(data);
  if (!items.length) return null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-2 flex items-baseline gap-2">
        <h3 className="text-sm font-semibold text-gray-900">Data hygiene</h3>
        <span className="text-xs text-gray-400">Worth fixing in the sheet before the next check-in</span>
      </div>
      <div className="space-y-2">
        {items.map((i) => (
          <div key={i.t} className="flex items-start gap-2">
            <span className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: DOT[i.sev] }} />
            <div>
              <div className="text-sm text-gray-900">{i.t}</div>
              <div className="text-xs text-gray-500">{i.s}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
